/**
 * Storefront helpers for reward-bar When / Products-to-offer modes that
 * need Ajax catalog lookups (collections, all-except, tag/vendor/title).
 */

import {
  REWARD_BAR_PRODUCT_SELECTION_MAX,
  REWARD_TIER_FREE_GIFT_DISCOUNT_DEFAULT,
} from "../../shared/reward-bar.js";
import { extractShopifyNumericId } from "../../shared/checkout-tracking.js";

function collectionHandle(value) {
  if (value && typeof value === "object") {
    return String(value.handle || "")
      .trim()
      .toLowerCase();
  }
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (!raw || raw === "[object object]") return "";
  if (/^\d+$/.test(raw) || /collection\//i.test(raw)) return "";
  return raw;
}

function normalizeCollectionId(value) {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    return normalizeCollectionId(value.id);
  }
  const raw = String(value).trim();
  if (!raw || raw === "[object Object]") return "";
  const gidMatch = raw.match(/Collection\/(\d+)/i);
  if (gidMatch) return gidMatch[1];
  return raw.replace(/^gid:\/\/shopify\/Collection\//i, "");
}

function normalizeProductId(value) {
  return extractShopifyNumericId(value) || "";
}

function mapAjaxProductToRewardProduct(product) {
  if (!product || product.id == null) return null;
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const firstVariant = variants[0];
  const featured =
    product.featured_image ||
    product.images?.[0]?.src ||
    product.image?.src ||
    "";
  return {
    id: String(product.id),
    title: product.title || "",
    handle: product.handle || "",
    vendor: product.vendor || "",
    tags: Array.isArray(product.tags)
      ? product.tags
      : typeof product.tags === "string"
        ? product.tags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
        : [],
    featured_image: featured,
    image: featured,
    variant_id: firstVariant?.id != null ? String(firstVariant.id) : "",
    variants: variants.map((variant) => ({
      id: variant?.id != null ? String(variant.id) : "",
      title: variant?.title || "",
      price: variant?.price,
      available: variant?.available !== false,
    })),
    discount_type: "percentage",
    discount_value: REWARD_TIER_FREE_GIFT_DISCOUNT_DEFAULT,
    discount_message: "",
  };
}

let collectionsIndexCache = null;
let collectionsIndexPromise = null;

async function getCollectionsIndex() {
  if (Array.isArray(collectionsIndexCache)) return collectionsIndexCache;
  if (collectionsIndexPromise) return collectionsIndexPromise;
  collectionsIndexPromise = (async () => {
    try {
      const res = await fetch("/collections.json?limit=250", {
        credentials: "same-origin",
      });
      if (!res.ok) {
        collectionsIndexCache = [];
        return collectionsIndexCache;
      }
      const json = await res.json();
      collectionsIndexCache = Array.isArray(json?.collections)
        ? json.collections
        : [];
      return collectionsIndexCache;
    } catch (_) {
      collectionsIndexCache = [];
      return collectionsIndexCache;
    } finally {
      collectionsIndexPromise = null;
    }
  })();
  return collectionsIndexPromise;
}

/** Resolve a storefront collection handle (picker may only persist id). */
async function resolveCollectionHandle(col) {
  const direct = collectionHandle(col);
  if (direct) return direct;
  const id = normalizeCollectionId(col);
  if (!id) return "";
  const index = await getCollectionsIndex();
  const match = (index || []).find(
    (entry) => normalizeCollectionId(entry?.id) === id
  );
  return collectionHandle(match) || "";
}

async function fetchCollectionProducts(handleOrId) {
  if (!handleOrId) return [];
  try {
    const res = await fetch(
      `/collections/${encodeURIComponent(handleOrId)}/products.json?limit=250`,
      { credentials: "same-origin" }
    );
    if (!res.ok) return [];
    const json = await res.json();
    return Array.isArray(json?.products) ? json.products : [];
  } catch (_) {
    return [];
  }
}

async function fetchProductsForCollection(col) {
  const handle = await resolveCollectionHandle(col);
  const id = normalizeCollectionId(col);
  if (handle) {
    const byHandle = await fetchCollectionProducts(handle);
    if (byHandle.length) return { handle, id, products: byHandle };
  }
  // Some themes accept numeric collection id in the Ajax path.
  if (id) {
    const byId = await fetchCollectionProducts(id);
    if (byId.length) return { handle: handle || id, id, products: byId };
  }
  return { handle, id, products: [] };
}

async function fetchStoreProductsPage(limit = 250) {
  try {
    const res = await fetch(`/products.json?limit=${Math.min(250, limit)}`, {
      credentials: "same-origin",
    });
    if (!res.ok) {
      // Fallback used by many themes
      const allRes = await fetch(
        `/collections/all/products.json?limit=${Math.min(250, limit)}`,
        { credentials: "same-origin" }
      );
      if (!allRes.ok) return [];
      const allJson = await allRes.json();
      return Array.isArray(allJson?.products) ? allJson.products : [];
    }
    const json = await res.json();
    return Array.isArray(json?.products) ? json.products : [];
  } catch (_) {
    return [];
  }
}

function matchTextValues(raw) {
  if (Array.isArray(raw)) {
    return raw
      .map((item) =>
        typeof item === "string"
          ? item.trim()
          : String(item?.title || item?.tag || item?.vendor || item?.id || "").trim()
      )
      .filter(Boolean);
  }
  if (typeof raw === "string") {
    return raw
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  return [];
}

function productMatchesTextOffer(product, type, value) {
  const needles = matchTextValues(value).map((v) => v.toLowerCase());
  if (!needles.length) return false;
  if (type === "product_specific_title") {
    const title = String(product?.title || "").toLowerCase();
    return needles.some((n) => title.includes(n));
  }
  if (type === "product_specific_vendor") {
    const vendor = String(product?.vendor || "").toLowerCase();
    return needles.some((n) => vendor === n);
  }
  if (type === "product_specific_tag") {
    const tags = (
      Array.isArray(product?.tags)
        ? product.tags
        : typeof product?.tags === "string"
          ? product.tags.split(",")
          : []
    ).map((t) => String(t).trim().toLowerCase());
    return needles.some((n) => tags.includes(n));
  }
  return false;
}

/**
 * Build collection → product-id Set map for When matching.
 */
export async function preloadRewardBarCollectionMembership(setting) {
  const map = Object.create(null);
  const seenHandles = Object.create(null);
  const tiers = Array.isArray(setting?.tiers) ? setting.tiers : [];

  const collections = [];
  if (setting?.exclude?.type === "product_from_collection") {
    for (const col of Array.isArray(setting.exclude.value)
      ? setting.exclude.value
      : []) {
      collections.push(col);
    }
  }
  for (const tier of tiers) {
    if (tier?.when?.type === "product_from_collection") {
      for (const col of Array.isArray(tier.when.value) ? tier.when.value : []) {
        collections.push(col);
      }
    }
    if (tier?.products_to_offer?.type === "product_from_collection") {
      for (const col of Array.isArray(tier.products_to_offer.value)
        ? tier.products_to_offer.value
        : []) {
        collections.push(col);
      }
    }
  }

  for (const col of collections) {
    const { handle, id: collectionId, products } =
      await fetchProductsForCollection(col);
    const cacheKey = handle || (collectionId ? `id-only:${collectionId}` : "");
    if (!cacheKey) continue;
    if (seenHandles[cacheKey]) {
      if (collectionId && handle) {
        map[`id:${collectionId}`] = map[`handle:${handle}`];
      }
      continue;
    }
    seenHandles[cacheKey] = true;
    const idSet = new Set();
    for (const product of products) {
      const pid = normalizeProductId(product?.id);
      if (pid) idSet.add(pid);
    }
    if (handle) map[`handle:${handle}`] = idSet;
    if (collectionId) map[`id:${collectionId}`] = idSet;
  }

  return map;
}

async function resolveCollectionGiftPool(collections) {
  const byId = new Map();
  for (const col of Array.isArray(collections) ? collections : []) {
    const { products } = await fetchProductsForCollection(col);
    for (const product of products) {
      const mapped = mapAjaxProductToRewardProduct(product);
      if (!mapped?.id || byId.has(mapped.id)) continue;
      byId.set(mapped.id, mapped);
      if (byId.size >= REWARD_BAR_PRODUCT_SELECTION_MAX) {
        return [...byId.values()];
      }
    }
  }
  return [...byId.values()];
}

async function resolveCatalogFilteredGiftPool(type, value) {
  const products = await fetchStoreProductsPage(250);
  const excludeIds = new Set();
  if (type === "all_except") {
    for (const entry of Array.isArray(value) ? value : []) {
      const pid = normalizeProductId(
        typeof entry === "object" ? entry?.id : entry
      );
      if (pid) excludeIds.add(pid);
    }
  }

  const out = [];
  for (const product of products) {
    const pid = normalizeProductId(product?.id);
    if (!pid) continue;
    if (type === "all_except") {
      if (excludeIds.has(pid)) continue;
    } else if (!productMatchesTextOffer(product, type, value)) {
      continue;
    }
    const mapped = mapAjaxProductToRewardProduct(product);
    if (!mapped) continue;
    out.push(mapped);
    if (out.length >= REWARD_BAR_PRODUCT_SELECTION_MAX) break;
  }
  return out;
}

/**
 * Resolve free-gift product pools for non-specific_product offer types.
 * @returns {Map<string, object[]>} tierId → products
 */
export async function resolveRewardBarGiftPools(setting) {
  const pools = new Map();
  const tiers = Array.isArray(setting?.tiers) ? setting.tiers : [];

  for (const tier of tiers) {
    if (!tier?.id) continue;

    const offerType = tier?.products_to_offer?.type || "specific_product";
    const offerValue = tier?.products_to_offer?.value;

    // Dynamic offer types always resolve from the storefront catalog —
    // ignore leftover tier.products from a prior specific_product config.
    if (offerType === "product_from_collection") {
      pools.set(String(tier.id), await resolveCollectionGiftPool(offerValue));
      continue;
    }

    if (
      offerType === "all_except" ||
      offerType === "product_specific_title" ||
      offerType === "product_specific_tag" ||
      offerType === "product_specific_vendor"
    ) {
      pools.set(
        String(tier.id),
        await resolveCatalogFilteredGiftPool(offerType, offerValue)
      );
      continue;
    }

    if (Array.isArray(tier.products) && tier.products.length) {
      pools.set(String(tier.id), tier.products);
      continue;
    }
    if (tier.product) {
      pools.set(String(tier.id), [tier.product]);
      continue;
    }

    if (offerType === "specific_product") {
      pools.set(
        String(tier.id),
        Array.isArray(offerValue) ? offerValue.filter((p) => p && p.id) : []
      );
      continue;
    }

    pools.set(String(tier.id), []);
  }

  return pools;
}

function indexProductMeta(map, product) {
  const pid = normalizeProductId(product?.id);
  if (!pid || map[pid]) return;
  const tags = Array.isArray(product.tags)
    ? product.tags
    : typeof product.tags === "string"
      ? product.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean)
      : [];
  map[pid] = {
    tags,
    vendor: product.vendor || "",
  };
}

/**
 * /cart.js lines omit tags — preload product id → { tags, vendor } for When=tag.
 */
export async function preloadRewardBarProductMeta(setting) {
  const tiers = Array.isArray(setting?.tiers) ? setting.tiers : [];
  const needsTags = tiers.some(
    (tier) => tier?.when?.type === "product_specific_tag"
  );
  if (!needsTags) return Object.create(null);

  const map = Object.create(null);
  const catalog = await fetchStoreProductsPage(250);
  for (const product of catalog) indexProductMeta(map, product);

  // Also index collection products referenced by When / offer (broader coverage).
  const collections = [];
  for (const tier of tiers) {
    if (tier?.when?.type === "product_from_collection") {
      for (const col of Array.isArray(tier.when.value) ? tier.when.value : []) {
        collections.push(col);
      }
    }
    if (tier?.products_to_offer?.type === "product_from_collection") {
      for (const col of Array.isArray(tier.products_to_offer.value)
        ? tier.products_to_offer.value
        : []) {
        collections.push(col);
      }
    }
  }
  for (const col of collections) {
    const { products } = await fetchProductsForCollection(col);
    for (const product of products) indexProductMeta(map, product);
  }

  // Tag-when alone: pull silver/all collections as a safety net for popular catalogs.
  if (!collections.length) {
    const allProducts = await fetchCollectionProducts("all");
    for (const product of allProducts) indexProductMeta(map, product);
  }

  return map;
}

export function rewardBarSettingNeedsStorefrontResolve(setting) {
  if (
    setting?.exclude?.type === "product_from_collection" &&
    Array.isArray(setting.exclude.value) &&
    setting.exclude.value.length
  ) {
    return true;
  }
  const tiers = Array.isArray(setting?.tiers) ? setting.tiers : [];
  return tiers.some((tier) => {
    const whenType = tier?.when?.type;
    if (whenType === "product_from_collection") return true;
    if (whenType === "product_specific_tag") return true;
    const offerType = tier?.products_to_offer?.type || "specific_product";
    if (offerType === "specific_product") {
      return !(
        (Array.isArray(tier?.products) && tier.products.length) ||
        tier?.product
      );
    }
    return (
      offerType === "product_from_collection" ||
      offerType === "all_except" ||
      offerType === "product_specific_title" ||
      offerType === "product_specific_tag" ||
      offerType === "product_specific_vendor"
    );
  });
}

export function buildRewardBarContextCacheKey(setting) {
  const tiers = Array.isArray(setting?.tiers) ? setting.tiers : [];
  const excludeKey = [
    setting?.exclude?.type || "",
    JSON.stringify(setting?.exclude?.value || []),
  ].join(":");
  const tierKey = tiers
    .map((tier) =>
      [
        tier?.id,
        tier?.when?.type,
        JSON.stringify(tier?.when?.value || []),
        tier?.products_to_offer?.type,
        JSON.stringify(tier?.products_to_offer?.value || []),
        Array.isArray(tier?.products) ? tier.products.length : 0,
      ].join(":")
    )
    .join("|");
  return `${excludeKey}|${tierKey}`;
}
